import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import type { UseMutationResult } from '@tanstack/react-query';
import GoogleLogoIcon from '../icons/GoogleLogoIcon';
import { EASE_OUT, DURATION_NORMAL } from '@/lib/motion';
import type { AuthorizeResponse } from '../../types/integrations';

type StartConnectMutation = UseMutationResult<
  { status: 'success'; data: AuthorizeResponse },
  unknown,
  void,
  unknown
>;

interface ConnectGoogleEmptyStateProps {
  startConnect: StartConnectMutation;
  settingsHref?: string;
}

const ConnectGoogleEmptyState: React.FC<ConnectGoogleEmptyStateProps> = ({
  startConnect,
  settingsHref = '/settings?tab=integrations',
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: DURATION_NORMAL, ease: EASE_OUT }}
      className="card-elevated px-8 py-12 max-w-xl mx-auto text-center"
    >
      <div className="w-16 h-16 mx-auto rounded-3 bg-surface-primary border border-border-primary/20 flex items-center justify-center mb-6">
        <GoogleLogoIcon className="w-9 h-9" />
      </div>
      <h2 className="text-xl font-bold text-text-primary mb-2">
        Connect your Google Calendar
      </h2>
      <p className="text-sm text-text-secondary mb-6">
        See your events in Tereiko, sync course schedules, and invite learners
        straight from Google.
      </p>
      <button
        type="button"
        onClick={() => startConnect.mutate()}
        disabled={startConnect.isPending}
        className="btn btn-primary active:scale-[0.97] transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {startConnect.isPending ? 'Opening Google…' : 'Connect Google Calendar'}
      </button>
      <p className="mt-4 text-xs text-text-tertiary">
        You can also manage this in{' '}
        <Link to={settingsHref} className="text-primary-500 hover:underline">
          Settings → Integrations
        </Link>
        .
      </p>
    </motion.div>
  );
};

export default ConnectGoogleEmptyState;
